import { WordList } from "./wordlist.js";
import * as Utils from "./utils.js";
import { Globals } from "./globals.js";
import * as constants from "./constants.js";
import { SGSprite } from "./sgsprite.js";

export class Trigger {
    constructor(scene, timestamp, params) {
        this.scene = scene;
        this.triggered = false; // don't think this is ever used...
        this.expired = false;
        this.nextUpdate = 0;
        this.triggerTime = 0;
        this.createTime = timestamp;
        this.params = params;
        this.expanded = null;
    }

    fired(timestamp) {
        return false;
    }

    expandAll(input) {
        let expanded = this.scene.varList.expandVars(input);
        expanded = Utils.evaluate(expanded);
        return new WordList(expanded, 0);
    }
}


/**************************************************************************************************

    ######  ########    ###    ########  ######## 
   ##    ##    ##      ## ##   ##     ##    ##    
   ##          ##     ##   ##  ##     ##    ##    
    ######     ##    ##     ## ########     ##    
         ##    ##    ######### ##   ##      ##    
   ##    ##    ##    ##     ## ##    ##     ##    
    ######     ##    ##     ## ##     ##    ##    

**************************************************************************************************/


export class Begin extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
    }

    fired(timestamp) {
        if (this.expired || this.scene.state != constants.SCENE_READY) {
            return false;
        }
        this.triggered = true;
        this.expired = true;
        return true;
    }
}

/**************************************************************************************************

    ######  ######## ######## ##     ## ########  
   ##    ## ##          ##    ##     ## ##     ## 
   ##       ##          ##    ##     ## ##     ## 
    ######  ######      ##    ##     ## ########  
         ## ##          ##    ##     ## ##        
   ##    ## ##          ##    ##     ## ##        
    ######  ########    ##     #######  ##        

**************************************************************************************************/

export class Setup extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
    }

    fired(timestamp) {
        if (this.expired || this.scene.state != constants.SCENE_LOADED) {
            return false;
        }
        this.triggered = true;
        this.expired = true;
        return true;
    }
}

/**************************************************************************************************
   #                               
  # #   ###### ##### ###### #####  
 #   #  #        #   #      #    # 
#     # #####    #   #####  #    # 
####### #        #   #      #####  
#     # #        #   #      #   #  
#     # #        #   ###### #    # 
                                   
**************************************************************************************************/

export class After extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
        this.triggerTime = null;
    }

    fired(timestamp) {
        if (this.expired || this.scene.state != constants.SCENE_RUNNING) {
            return false;
        }
        // expand on first use
        if (this.expanded == null) {
            this.expanded = this.expandAll(this.params);
            const duration = this.expanded.getDuration(1);
            if (duration) {
                this.triggerTime = this.createTime + (duration * 1000);
            }
        }
        if (this.triggerTime !== null && timestamp > this.triggerTime) {
            this.triggered = true;
            this.expired = true;
            return true;
        }
        return false;
    }
}


/**************************************************************************************************

   ######## ##     ## ######## ########  ##    ## 
   ##       ##     ## ##       ##     ##  ##  ##  
   ##       ##     ## ##       ##     ##   ####   
   ######   ##     ## ######   ########     ##    
   ##        ##   ##  ##       ##   ##      ##    
   ##         ## ##   ##       ##    ##     ##    
   ########    ###    ######## ##     ##    ##    

**************************************************************************************************/

export class Every extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
        this.triggerRate = null;
        this.last_triggered = timestamp;
    }

    fired(timestamp) {
        if (this.scene.state != constants.SCENE_RUNNING) {
            return false;
        }
        // expand on first use
        if (this.expanded == null) {
            this.expanded = this.expandAll(this.params);
            const duration = this.expanded.getDuration(1);
            if (duration) {
                this.triggerRate = duration * 1000;
            }
        }
        if (this.triggerRate !== null && (timestamp - this.last_triggered) > this.triggerRate) {
            this.triggered = true;
            this.last_triggered = timestamp;
            return true;
        } // else
        this.triggered = false;
        return false;
    }
}


/**************************************************************************************************

   ##      ## ##     ## ######## ##    ## 
   ##  ##  ## ##     ## ##       ###   ## 
   ##  ##  ## ##     ## ##       ####  ## 
   ##  ##  ## ######### ######   ## ## ## 
   ##  ##  ## ##     ## ##       ##  #### 
   ##  ##  ## ##     ## ##       ##   ### 
    ###  ###  ##     ## ######## ##    ## 

**************************************************************************************************/

export class When extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
        this.nextCheck = 0;
    }

    fired(timestamp) {
        if (this.scene.state != constants.SCENE_RUNNING) {
            return false;
        }
        if (this.triggered)  {
            if (this.nextCheck > timestamp) {
                // we have triggered recently, come back later
                return false;
            } else {
                // OK, start checking again
                this.triggered = false;
            }
        }
        let result = false;
        // use the raw parameters and expand on *every* use
        let expanded = this.expandAll(this.params);
        result = Utils.logical(expanded);
        if (result) {
            this.triggered = true;
            this.nextCheck = timestamp + (1000 * 60);
            return true
        } // else
        this.triggered = false;
        return false;
    }
}


/**************************************************************************************************

      ###    ######## 
     ## ##      ##    
    ##   ##     ##    
   ##     ##    ##    
   #########    ##    
   ##     ##    ##    
   ##     ##    ##    

**************************************************************************************************/

export class AtClass extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
        this.minutes = null;
        this.hours = null;
        this.seconds = 0;
        this.nextCheck = 0;
        this.valid = true;
    }

    fired(timestamp) {
        if (this.scene.state != constants.SCENE_RUNNING) {
            return false;
        }
        if (!this.valid) {
            return false;
        }
        // expand on first use
        if (this.expanded == null) {
            this.expanded = this.expandAll(this.params);
            const timeOfDay = this.expanded.getWord();
            if (timeOfDay.match(/^(?:[01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/)) { // HH:MM:SS only
                const parts = timeOfDay.split(":");
                this.hours = parseInt(parts[0]);
                this.minutes = parseInt(parts[1]);
                if (parts.length > 2) {
                    this.seconds = parseInt(parts[2]);
                }
            } else {
                Globals.log.error("Incorrect time format " + timeOfDay);
                this.valid = false;
            }
        }
        if (this.nextCheck > timestamp) {
            // try again later
            return false;
        }
        const d = new Date();
        d.setTime(timestamp);
        let matched = true;
        if (this.hours != d.getHours()) {
            matched = false;
        }
        if (this.minutes != d.getMinutes()) {
            matched = false;
        }
        if (this.seconds != d.getSeconds()) {
            matched = false;
        }
        if (matched) { // If triggered, leave it for a while, as we can only trigger once per day at most
            this.nextCheck = timestamp + (60 * 60 * 1000); // wait an hour
        }
        return matched;
    }
}

/**************************************************************************************************

   ########    ###     ######  ##     ## 
   ##         ## ##   ##    ## ##     ## 
   ##        ##   ##  ##       ##     ## 
   ######   ##     ## ##       ######### 
   ##       ######### ##       ##     ## 
   ##       ##     ## ##    ## ##     ## 
   ######## ##     ##  ######  ##     ## 

**************************************************************************************************/

export class Each extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
        this.matchString = "**:**:**";
        this.nextCheck = 0;
    }

    fired(timestamp) {
        if (this.expired || this.scene.state != constants.SCENE_RUNNING) {
            return false;
        }
        // expand on first use
        if (this.expanded == null) {
            this.expanded = this.expandAll(this.params);
            const candidate = this.expanded.getWord();
            if (candidate.match(/^(?:[01*][0-9*]|2[0-3*]|\*[0-9*]):[0-5*][0-9*](:[0-5*][0-9*])?$/)) {
                if (candidate.length == 5) {
                    this.matchString = candidate + ":00";
                } else {
                    this.matchString = candidate;
                }
            } else {
                    Globals.log.error("Incorrect time format- " + candidate);
                    this.expired = true; // prevent running if not valid time
                    return false;
            }
        }
        // Do we need to run yet?
        if (this.nextCheck > timestamp) {
            return false;
        }
        // Now check for a match
        const d = new Date(timestamp);
        const timeString = d.toTimeString();
        let matched = true;
        for (let i = 0; i < this.matchString.length; i++ ) {
            let match = this.matchString.charAt(i);
            if (match == "*") {
                continue;
            }
            if (match != timeString.charAt(i)) {
                matched = false;
                break;
            }
        }
        // check again in another second
        this.nextCheck = timestamp + 1000;

        return matched;
    }
}

/**************************************************************************************************

      ###    ######## ######## ##    ## ########  
     ## ##      ##    ##       ###   ## ##     ## 
    ##   ##     ##    ##       ####  ## ##     ## 
   ##     ##    ##    ######   ## ## ## ##     ## 
   #########    ##    ##       ##  #### ##     ## 
   ##     ##    ##    ##       ##   ### ##     ## 
   ##     ##    ##    ######## ##    ## ########  

**************************************************************************************************/

export class AtEnd extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
    }

    fired(timestamp) {
        if (this.expired || this.scene.state != constants.SCENE_FINISHED) {
            return false;
        }
        // triggered when the scene is "deleted"
        this.expired = true;
        return true;
    }
}

/**************************************************************************************************

    ######  ##       ####  ######  ##    ## 
   ##    ## ##        ##  ##    ## ##   ##  
   ##       ##        ##  ##       ##  ##   
   ##       ##        ##  ##       #####    
   ##       ##        ##  ##       ##  ##   
   ##    ## ##        ##  ##    ## ##   ##  
    ######  ######## ####  ######  ##    ## 

**************************************************************************************************/

export class OnClick extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
        // this.sgSprite = scene.getSprite(params); // for local only sprites
    }

    fired(timestamp) {
        if (this.scene.state != constants.SCENE_RUNNING) {
            return false;
        }
        if (!this.sgSprite) {
            this.sgSprite = SGSprite.getSprite(this.scene.name, this.params, false);
            if (this.sgSprite && this.sgSprite.piSprite) {
                this.sgSprite.piSprite.eventMode = "dynamic";
                this.sgSprite.piSprite.onclick = this.sgSprite.callback();
            }
        }
        if (!this.sgSprite) {
            return false; // not created yet
        }
        if (this.sgSprite.event) {
            this.sgSprite.event = false;
            return true;
        } // else
        return false;
    }
}

/**************************************************************************************************

    #######  ##    ## ##    ## ######## ##    ## 
   ##     ## ###   ## ##   ##  ##        ##  ##  
   ##     ## ####  ## ##  ##   ##         ####   
   ##     ## ## ## ## #####    ######      ##    
   ##     ## ##  #### ##  ##   ##          ##    
   ##     ## ##   ### ##   ##  ##          ##    
    #######  ##    ## ##    ## ########    ##    

**************************************************************************************************/

export class OnKey extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
        this.key = params.charAt(0);
    }

    fired(timestamp) {
        if (this.scene.state != constants.SCENE_RUNNING) {
            return false;
        }
        if (this.key == Globals.lastKey) {
            Globals.lastKey = null; // only supports one trigger per key - TBD
            return true;
        } // else
        return false;
    }
}

/**************************************************************************************************

    #######  ##    ##  ######     ###    ##       ##       
   ##     ## ###   ## ##    ##   ## ##   ##       ##       
   ##     ## ####  ## ##        ##   ##  ##       ##       
   ##     ## ## ## ## ##       ##     ## ##       ##       
   ##     ## ##  #### ##       ######### ##       ##       
   ##     ## ##   ### ##    ## ##     ## ##       ##       
    #######  ##    ##  ######  ##     ## ######## ######## 

**************************************************************************************************/

export class OnCall extends Trigger {
    constructor(scene, timestamp, params) {
        super(scene, timestamp, params);
        this.callName = params;
        this.called = false;
    }

    fired(timestamp) {
        if (this.called) {
            this.called = false;
            return true;
        } // else
        return false;
    }
}
